import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
    useLocaleState,
    useTranslate,
    useTheme,
    useRecordContext,
    useUpdate,
    useNotify,
    useRefresh,
} from "react-admin";
import { CreateCarnetDialog } from "./carnetTable/CreateCarnetDialog";
import { EditCarnetDialog } from "./carnetTable/EditCarnetDialog";
import { ConfirmationDialog } from "shared/src/components/dialogs/ConfirmationDialog";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { roundUpAndReturnNum } from "../../utils/roundUpAndReturnNum";

export const CarnetTable = ({ equipData }) => {
    const [locale] = useLocaleState();
    const [update] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const tableContainerRef = useRef(null);
    const [theme] = useTheme();
    const [editCarnetItem, setEditCarnetItem] = useState(null);
    const [showCreateCarnetDialog, setShowCreateCarnetDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [totals, setTotals] = useState({
        totalCarnetPrice: 0,
        totalWeight: 0,
    });
    const record = useRecordContext();

    const columns = [
        {
            id: "description",
            label: translate("equip.description"),
            format: (item) => `
            ${item.product.name[locale]}
            ${item.product.description[locale]}`,
        },
        { id: "labelNumber", label: translate("equip.labelNumber") },
        {
            id: "serialNumber",
            label: translate("equip.serialNumber"),
            format: (item) => item.sku?.serialNumber || "N/A",
        },
        {
            id: "quantity",
            label: translate("equip.quantity"),
            format: (item) => item?.carnetData?.quantity || item.quantity,
        },
        {
            id: "weight",
            label: translate("equip.weight"),
            format: (item) => calcWeight(item).toFixed(2),
        },
        {
            id: "carnetPrice",
            label: translate("equip.value"),
            format: (item) => calcCarnetPrice(item).toFixed(2),
        },
        {
            id: "originCountry",
            label: translate("equip.originCountry"),
            format: (item) => item.product.originCountry,
        },
        { label: "" },
        { label: "" },
    ];

    const deleteAdditionalRow = () => {
        const updatedCarnetData = record.carnetData.filter(
            (_, i) => i !== itemToDelete.index
        );
        setItemToDelete(null);
        update(
            "rentals",
            {
                id: record.id,
                data: {
                    carnetData: updatedCarnetData,
                },
            },
            {
                onSettled: () => {
                    refresh();
                },
                onError: (e) => {
                    handleHttpError(e, notify);
                },
            }
        );
    };

    useEffect(() => {
        if (record) {
            const calculateTotals = (items) =>
                items.reduce(
                    (acc, item) => ({
                        totalCarnetPrice:
                            acc.totalCarnetPrice + calcCarnetPrice(item),
                        totalWeight: acc.totalWeight + calcWeight(item),
                    }),
                    { totalCarnetPrice: 0, totalWeight: 0 }
                );

            const rentalItemsTotals = calculateTotals(equipData ?? []);
            const additionalItemsTotals = calculateTotals(
                record.carnetData ?? []
            );

            const totalCarnetPrice = roundUpAndReturnNum(
                rentalItemsTotals.totalCarnetPrice +
                    additionalItemsTotals.totalCarnetPrice
            );

            const totalWeight = roundUpAndReturnNum(
                rentalItemsTotals.totalWeight +
                    additionalItemsTotals.totalWeight
            );

            setTotals({
                totalCarnetPrice,
                totalWeight,
            });
        }
    }, [equipData, record]);

    return (
        <>
            <Paper
                sx={{
                    margin: 1.5,
                    position: "relative",
                    "> div:first-of-type": {
                        overflow: "unset !important",
                    },
                    minWidth: "1000px",
                }}
            >
                <Button onClick={() => setShowCreateCarnetDialog(true)}>
                    {translate("equip.addCarnetItem")}
                </Button>
                <CreateCarnetDialog
                    open={showCreateCarnetDialog}
                    setShowCreateCarnetDialog={setShowCreateCarnetDialog}
                />
                <ConfirmationDialog
                    open={!!itemToDelete}
                    label={translate("equip.confirmDeleteCarnetRow")}
                    onCancel={() => setItemToDelete(null)}
                    onConfirm={() => deleteAdditionalRow()}
                />
                {editCarnetItem && (
                    <EditCarnetDialog
                        record={editCarnetItem.record}
                        isAdditionalRow={editCarnetItem.isAdditionalRow}
                        setEditCarnetItem={setEditCarnetItem}
                        index={editCarnetItem.index}
                    />
                )}
                <TableContainer ref={tableContainerRef}>
                    <Table>
                        <TableHead
                            sx={{
                                position: "sticky",
                                width: "100%",
                                top: 0,
                                zIndex: 10,
                                backgroundColor:
                                    theme === "dark" ? "#1e1e1e" : "#fff",
                            }}
                        >
                            <TableRow key={1}>
                                {columns.map((column, index) => {
                                    return (
                                        <TableCell
                                            key={"head_col_" + index}
                                            sx={{
                                                fontWeight: "bolder",
                                            }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {record?.carnetData
                                ?.toReversed()
                                .map((carnetItem, index) => (
                                    <TableRow
                                        sx={{
                                            backgroundColor:
                                                theme === "dark"
                                                    ? "#343434 !important"
                                                    : "#f5f5f5",
                                        }}
                                        key={"add_row_" + index}
                                    >
                                        {columns.map(
                                            (column, i) =>
                                                column.id && (
                                                    <TableCell
                                                        key={`add_row_${index}
                                                    _col_${i}`}
                                                    >
                                                        {column.id === "weight"
                                                            ? calcWeight(
                                                                  carnetItem
                                                              ).toFixed(2)
                                                            : column.id ===
                                                              "carnetPrice"
                                                            ? calcCarnetPrice(
                                                                  carnetItem
                                                              ).toFixed(2)
                                                            : carnetItem[
                                                                  column.id
                                                              ]}
                                                    </TableCell>
                                                )
                                        )}
                                        <TableCell>
                                            <Button
                                                onClick={() =>
                                                    setEditCarnetItem({
                                                        record: carnetItem,
                                                        isAdditionalRow: true,
                                                        index,
                                                    })
                                                }
                                            >
                                                {translate("equip.edit")}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                color="error"
                                                onClick={() =>
                                                    setItemToDelete({
                                                        index:
                                                            record.carnetData
                                                                .length -
                                                            1 -
                                                            index,
                                                    })
                                                }
                                            >
                                                {translate("equip.delete")}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {equipData.map((row, index) => (
                                <TableRow
                                    hover
                                    tabIndex={-1}
                                    key={"prime_row_" + index}
                                    sx={{
                                        position: "relative",
                                        maxHeight: "73px",
                                        overflow: "visible",
                                    }}
                                >
                                    {columns.map((column, i) => {
                                        const value = row[column.id];
                                        return (
                                            column.id && (
                                                <TableCell
                                                    key={`prime_row_${index}
                                                    _col_${i}`}
                                                >
                                                    <Box>
                                                        {column.format
                                                            ? column.format(row)
                                                            : value
                                                            ? value
                                                            : ""}
                                                    </Box>
                                                </TableCell>
                                            )
                                        );
                                    })}
                                    <TableCell>
                                        <Button
                                            onClick={() =>
                                                setEditCarnetItem({
                                                    record: row,
                                                    isAdditionalRow: false,
                                                })
                                            }
                                        >
                                            {translate("equip.edit")}
                                        </Button>
                                    </TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {translate("equip.total")}
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {totals.totalWeight.toFixed(2)}
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {totals.totalCarnetPrice.toFixed(2)}
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
};

const calcWeight = (item) => {
    const weightPerItem = Number(
        item.carnetData?.weight ||
            item.sku?.weight ||
            item.product?.weight ||
            item.weight ||
            0
    );
    const quantity = Number(item?.carnetData?.quantity || item.quantity || 0);
    const totalItemWeight = weightPerItem * quantity;
    return roundUpAndReturnNum(totalItemWeight);
};

const calcCarnetPrice = (item) => {
    const pricePerItem = Number(
        item.carnetData?.carnetPrice ||
            (item.product && item.product.carnetPrice) ||
            item.carnetPrice ||
            0
    );
    const quantity = Number(item?.carnetData?.quantity || item.quantity || 0);
    const totalPrice = pricePerItem * quantity;
    return roundUpAndReturnNum(totalPrice);
};
